import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Col, Form, Row } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { schemaFormProject } from "../../../features/forms/schemas/schemaFormProject";
import { Project } from "../../../types/projects";
import { Site } from "../../../types/sites";
import { Role, UserForList } from "../../../types/user";
import { isDev } from "../../../utils/dev";
import Br from "../../ui/Br";
import spacing from "../../ui/spacingConfig";
import FieldDate from "../fields/FieldDate";
import FieldInput from "../fields/FieldInput";
import FieldMultiSelect from "../fields/FieldMultiSelect";
import FieldSelect from "../fields/FieldSelect";
import FieldTextArea from "../fields/FieldTextArea";
import FieldUpload from "../fields/FieldUpload/FieldUpload";
import useSubmit from "./useSubmit";

export type FormProjectSchema = z.infer<typeof schemaFormProject>;

type Props = {
  isEditMode?: boolean;
  project?: Project;
  sites?: Site[];
  users?: UserForList[];
  roles?: Role[];
};

const getDefaultValue = (edit: boolean, project?: Project): FormProjectSchema => {
  if (!edit || !project) {
    return {
      eventType: "PROJECT",
      name: "",
      description: "",
      firstMeasure: "",
      site: "",
      localisation: "",
      dateEvt: dayjs(new Date()),
      accessGivenTo: [],
      accessRoleGivenTo: [],
    };
  }
  return {
    eventType: "PROJECT",
    name: project.name || "",
    description: project.description || "",
    firstMeasure: project.firstMeasure,
    site: project.site ? String(project.site.id) : "",
    localisation: project.localisation,
    dateEvt: dayjs(project.dateEvt),
  };
};

const FormProjectNew: React.FC<Props> = ({
  isEditMode,
  project,
  sites,
  users,
  roles,
}) => {
  const { onSubmit, isLoading } = useSubmit(isEditMode, project);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormProjectSchema>({
    defaultValues: getDefaultValue(!!isEditMode, project),
    resolver: zodResolver(schemaFormProject),
  });

  if (isDev) {
    const formValues = watch();
    console.log("formValues", formValues);
    console.log("errors", errors);
  }

  return (
    <Form
      labelCol={{ span: spacing.tiny }}
      wrapperCol={{ span: spacing.sPlus }}
      layout="horizontal"
      onFinish={handleSubmit(onSubmit)}
      labelAlign="left"
      colon={false}
    >
      <Row gutter={spacing.m}>
        <Col span={14}>
          <Card title="Description" bordered={false}>
            <FieldInput<FormProjectSchema>
              errorMessage={errors?.name?.message}
              control={control}
              name="name"
              label="Titre"
              placeholder="Titre..."
            />
            <FieldTextArea<FormProjectSchema>
              errorMessage={errors?.description?.message}
              control={control}
              name="description"
              label="Description"
              placeholder="Description..."
            />
            <FieldTextArea<FormProjectSchema>
              errorMessage={errors?.firstMeasure?.message}
              control={control}
              name="firstMeasure"
              label="Mesure immÃ©diate"
              placeholder="Mesure immÃ©diate..."
            />
          </Card>
        </Col>
        <Col span={10}>
          <Card title="Date et lieu" bordered={false}>
            <FieldSelect<FormProjectSchema>
              errorMessage={errors?.site?.message}
              control={control}
              name="site"
              label="Site"
              placeholder="Choisir un site"
              options={
                sites?.map((site) => ({
                  value: String(site.id),
                  label: site.name,
                })) || []
              }
            />
            <FieldInput<FormProjectSchema>
              errorMessage={errors?.localisation?.message}
              control={control}
              name="localisation"
              label="Localisation"
              placeholder="PrÃ©cision..."
            />
            <FieldDate<FormProjectSchema>
              errorMessage={errors?.dateEvt?.message}
              control={control}
              name="dateEvt"
              label="Date"
            />
          </Card>
        </Col>
      </Row>
      {!isEditMode && (
        <>
          <Br height={spacing.xl} />
          <Row gutter={spacing.m}>
            <Col span={14}>
              <Card title="PiÃ¨ces jointes" bordered={false}>
                <FieldUpload<FormProjectSchema>
                  errorMessage={errors.attachedFiles?.message}
                  name="attachedFiles"
                  control={control}
                  listType="picture"
                  label=""
                  placeholder=""
                />
              </Card>
            </Col>
            <Col span={10}>
              <Card title="AccÃ¨s" bordered={false}>
                <FieldMultiSelect<FormProjectSchema>
                  errorMessage={errors?.accessGivenTo?.message}
                  control={control}
                  name="accessGivenTo"
                  label="Utilisateurs"
                  placeholder="Donner accÃ¨s Ã  des utilisateurs"
                  options={
                    users?.map((user) => ({
                      value: String(user.id),
                      label: user.fullname,
                    })) || []
                  }
                />
                <FieldMultiSelect<FormProjectSchema>
                  errorMessage={errors?.accessRoleGivenTo?.message}
                  control={control}
                  name="accessRoleGivenTo"
                  label="RÃ´les"
                  placeholder="Donner accÃ¨s Ã  des rÃ´les"
                  options={
                    roles?.map((role) => ({
                      value: String(role.id),
                      label: role.displayName,
                    })) || []
                  }
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
      <Br height={spacing.xl} />
      <Row justify="end">
        <Button
          type="primary"
          htmlType="submit"
          className="large-padding-btn"
          loading={isLoading}
        >
          {isEditMode ? "Modifier" : "Publier"}
        </Button>
      </Row>
    </Form>
  );
};

export default FormProjectNew;
